import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import {
  LayoutDashboard,
  Search,
  User,
  Settings,
  PlusCircle,
  Users,
  LogOut,
  X,
  FileText,
  Package,
  Briefcase,
  Mail,
  Shield,
  Calendar,
  Zap,
} from "lucide-react";

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export default function Sidebar({ isOpen, setIsOpen }: SidebarProps) {
  const { user, logout } = useAuth();
  const { isDark } = useDarkMode();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  // Check if user is whitelisted
  const isWhitelisted =
    user?.subscriptionState?.subscriptions?.isWhitelisted ||
    user?.subscriptionState?.whitelist?.isActive ||
    user?.isWhitelisted;

  // Check if user can post jobs (either admin, whitelisted, or has job posting subscription)
  const canPostJobs =
    isAdmin ||
    isWhitelisted ||
    user?.subscriptionState?.capabilities?.canPostJobs;

  // Navigation items for regular users
  const userNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/find-job", label: "Find Jobs", icon: Search },
    {
      path: "/dashboard/applications",
      label: "My Applications",
      icon: FileText,
    },
    { path: "/dashboard/profile", label: "Profile", icon: User },
    { path: "/dashboard/settings", label: "Settings", icon: Settings },
    { path: "/dashboard/security", label: "Security", icon: Shield },
  ];

  // Add whitelist requests for non-whitelisted users
  if (!isWhitelisted && !isAdmin) {
    userNavItems.splice(3, 0, {
      path: "/dashboard/whitelist-requests",
      label: "Whitelist Requests",
      icon: Shield,
    });
  }

  // Navigation items for admins
  const adminNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/admin-panel", label: "Admin Panel", icon: Shield },
    { path: "/dashboard/create-job", label: "Create Jobs", icon: PlusCircle },
    {
      path: "/dashboard/create-event",
      label: "Create Event",
      icon: PlusCircle,
    },
    {
      path: "/dashboard/applications-whitelist",
      label: "View Applications",
      icon: FileText,
    },
    { path: "/dashboard/admin-packages", label: "Manage Plans", icon: Package },
    {
      path: "/dashboard/whitelist-management",
      label: "Whitelist Management",
      icon: Users,
    },
    { path: "/dashboard/contact-forms", label: "Contact Forms", icon: Mail },
    {
      path: "/dashboard/events-manager",
      label: "Manage Events",
      icon: Calendar,
    },
    { path: "/dashboard/find-job", label: "Find Jobs", icon: Search },
    { path: "/dashboard/profile", label: "Profile", icon: User },
    { path: "/dashboard/settings", label: "Settings", icon: Settings },
    { path: "/dashboard/security", label: "Security", icon: Shield },
  ];

  // Navigation items for whitelisted users (non-admins with whitelist access)
  const whitelistedNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/find-job", label: "Find Jobs", icon: Search },
    { path: "/dashboard/create-job", label: "Create Jobs", icon: PlusCircle },
    {
      path: "/dashboard/applications",
      label: "My Applications",
      icon: FileText,
    },
    {
      path: "/dashboard/whitelist-applications",
      label: "Whitelist Applications",
      icon: Users,
    },
    { path: "/dashboard/profile", label: "Profile", icon: User },
    { path: "/dashboard/settings", label: "Settings", icon: Settings },
    { path: "/dashboard/security", label: "Security", icon: Shield },
  ];

  // Determine which navigation items to show
  let navItems = userNavItems;
  if (isAdmin) {
    navItems = adminNavItems;
  } else if (isWhitelisted) {
    navItems = whitelistedNavItems;
  }

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      setIsOpen(false);
    }
  };

  // Close sidebar when clicking outside (for mobile)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsOpen(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector("[data-sidebar]");
      if (sidebar && !sidebar.contains(event.target as Node) && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        data-sidebar
        className={`fixed md:static inset-y-0 left-0 z-50 transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 transition-transform duration-300 ease-in-out flex flex-col w-64 border-r h-full ${
          isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-sky-500 to-sky-600 rounded-full flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-white" />
            </div>
            <h2
              className={`text-lg font-semibold ${
                isDark ? "text-white" : "text-gray-800"
              }`}
            >
              {isAdmin ? "Admin Panel" : "Dashboard"}
            </h2>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className={`p-2 rounded-md hover:bg-gray-100 md:hidden ${
              isDark
                ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* User Info */}
        <div
          className={`p-4 border-b ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium truncate ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {user?.name || "User"}
              </p>
              <div className="flex items-center gap-1">
                <p
                  className={`text-xs truncate capitalize ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {user?.role || "user"}
                </p>
                {isAdmin && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                    Admin
                  </span>
                )}
                {isWhitelisted && !isAdmin && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-green-500 text-white rounded-full flex items-center gap-1">
                    <Zap size={10} />
                    Whitelisted
                  </span>
                )}
                {canPostJobs && !isAdmin && !isWhitelisted && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                    Employer
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 group ${
                  active
                    ? "bg-sky-50 text-sky-600 border border-sky-200 shadow-sm dark:bg-sky-900/20 dark:text-sky-400 dark:border-sky-800"
                    : isDark
                      ? "text-gray-400 hover:bg-gray-800 hover:text-white"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <Icon
                  size={20}
                  className={active ? "text-sky-600 dark:text-sky-400" : ""}
                />
                <span className="font-medium">{item.label}</span>
                {active && (
                  <div className="w-1 h-6 bg-sky-500 rounded-full ml-auto"></div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div
          className={`p-4 border-t ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={() => {
              handleLogout();
              handleNavClick();
            }}
            className={`flex items-center space-x-3 w-full px-3 py-3 rounded-lg transition-all duration-200 ${
              isDark
                ? "text-gray-400 hover:text-red-400 hover:bg-red-900/20 border border-transparent hover:border-red-800"
                : "text-gray-600 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200"
            }`}
          >
            <LogOut size={20} />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>
    </>
  );
}
